import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import MarketplaceKPIs from "./MarketplaceKPIs";
import MarketplaceFilters from "./MarketplaceFilters";
import MarketplaceTable from "./MarketplaceTable";
import MarketplaceCharts from "./MarketplaceCharts";
import MarketplaceUploads from "./MarketplaceUploads";
import MarketplaceDealBreakdown from "./MarketplaceDealBreakdown";
import MarketplaceTagBreakdown from "./MarketplaceTagBreakdown";
import DealTagsManager from "./DealTagsManager";
import { listTags } from "./dealTagsApi";
import { Loader2, ShieldAlert } from "lucide-react";
import { useTheme } from "../../../ThemeContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const MarketplaceView = ({ token, requestDelete, isSuperAdmin }) => {
  const { light: lightMode } = useTheme();
  const [activeSection, setActiveSection] = useState("dashboard");
  const [summary, setSummary] = useState(null);
  const [breakdownBySSP, setBreakdownBySSP] = useState([]);
  const [trendData, setTrendData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState(null);
  const [permLoading, setPermLoading] = useState(true);

  // Default to Last 90 Days so the trend chart shows recent data on first load.
  const defaultRange = (() => {
    const today = new Date();
    const past = new Date();
    past.setUTCDate(past.getUTCDate() - 90);
    return {
      date_from: past.toISOString().split("T")[0],
      date_to: today.toISOString().split("T")[0],
    };
  })();
  const [filters, setFilters] = useState({
    ssp_source: "", dsp: "", deal_name: "", tag_ids: [], ...defaultRange,
  });
  const [tags, setTags] = useState([]);  // super_admin only

  // Fetch permissions on mount
  useEffect(() => {
    const fetchPerms = async () => {
      try {
        const res = await axios.get(`${API}/reporting/marketplace/my-permissions`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setPermissions(res.data);
      } catch {
        setPermissions({ has_access: false });
      } finally {
        setPermLoading(false);
      }
    };
    fetchPerms();
  }, [token]);

  // Super Admins load the tag catalog once for filter dropdowns / chip editors.
  const refreshTags = useCallback(async () => {
    if (!isSuperAdmin) return;
    try {
      const data = await listTags(token);
      setTags(data);
    } catch { /* silent — tags are optional */ }
  }, [token, isSuperAdmin]);
  useEffect(() => { refreshTags(); }, [refreshTags]);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const h = { Authorization: `Bearer ${token}` };
      const p = {};
      if (filters.ssp_source) p.ssp_source = filters.ssp_source;
      if (filters.dsp) p.dsp = filters.dsp;
      if (filters.deal_name) p.deal_name = filters.deal_name;
      if (filters.date_from) p.date_from = filters.date_from;
      if (filters.date_to) p.date_to = filters.date_to;
      if (filters.tag_ids && filters.tag_ids.length) p.tag_ids = filters.tag_ids.join(",");

      const [sumRes, sspRes, trendRes] = await Promise.all([
        axios.get(`${API}/reporting/marketplace/summary`, { headers: h, params: p }),
        axios.get(`${API}/reporting/marketplace/breakdowns/ssp_source`, { headers: h, params: p }),
        axios.get(`${API}/reporting/marketplace/breakdowns/date`, { headers: h, params: p }),
      ]);
      setSummary(sumRes.data);
      setBreakdownBySSP(sspRes.data);
      const sorted = (trendRes.data || []).sort((a, b) => a.label.localeCompare(b.label));
      setTrendData(sorted);
    } catch {
      toast.error("Failed to load marketplace data");
    } finally {
      setLoading(false);
    }
  }, [token, filters]);

  useEffect(() => {
    if (permissions?.has_access) fetchDashboard();
  }, [fetchDashboard, permissions]);

  const handleApplyFilters = (newFilters) => setFilters(newFilters);

  const handleExport = async () => {
    try {
      const p = {};
      if (filters.ssp_source) p.ssp_source = filters.ssp_source;
      if (filters.dsp) p.dsp = filters.dsp;
      if (filters.deal_name) p.deal_name = filters.deal_name;
      if (filters.date_from) p.date_from = filters.date_from;
      if (filters.date_to) p.date_to = filters.date_to;
      if (filters.tag_ids && filters.tag_ids.length) p.tag_ids = filters.tag_ids.join(",");
      const res = await axios.get(`${API}/reporting/marketplace/export`, {
        headers: { Authorization: `Bearer ${token}` }, params: p, responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `marketplace_export_${new Date().toISOString().split("T")[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch {
      toast.error("Export failed");
    }
  };

  if (permLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-6 h-6 text-[#ff3737] animate-spin" />
      </div>
    );
  }

  if (!permissions?.has_access) {
    return (
      <div className="text-center p-12">
        <ShieldAlert className="w-10 h-10 text-[#ebebeb]/20 mx-auto mb-3" />
        <p className="text-[#ebebeb]/50 font-mono text-sm">You don't have access to marketplace reports.</p>
        <p className="text-[#ebebeb]/30 font-mono text-xs mt-1">Contact your Super Admin to get access.</p>
      </div>
    );
  }

  if (!permissions?.has_deals && !permissions?.is_admin) {
    return (
      <div className="text-center p-12">
        <ShieldAlert className="w-10 h-10 text-[#ebebeb]/20 mx-auto mb-3" />
        <p className="text-[#ebebeb]/50 font-mono text-sm">No deals are associated with your account yet.</p>
        <p className="text-[#ebebeb]/30 font-mono text-xs mt-1">Marketplace data will appear once your deals have SSP deal names assigned.</p>
      </div>
    );
  }

  const revenueVisible = permissions?.revenue_visible ?? false;

  // Non-admins don't see Manage Uploads or Tags
  const sections = isSuperAdmin
    ? [
        { id: "dashboard", label: "Dashboard" },
        { id: "table", label: "Data Table" },
        { id: "tags", label: "Tags & Owners" },
        { id: "uploads", label: "Manage Uploads" },
      ]
    : [
        { id: "dashboard", label: "Dashboard" },
        { id: "table", label: "Data Table" },
      ];

  // Light mode theme overrides
  const t = lightMode
    ? { bg: "bg-white", card: "bg-white border-gray-200", text: "text-gray-900", muted: "text-gray-500", border: "border-gray-200", accent: "text-[#ff3737]" }
    : { bg: "", card: "", text: "", muted: "", border: "", accent: "" };

  return (
    <div className={`space-y-6 ${lightMode ? "bg-[#f5f5f7] -mx-4 lg:-mx-8 -mb-4 lg:-mb-8 px-4 lg:px-8 pb-4 lg:pb-8 pt-1 rounded-xl" : ""}`} data-testid="marketplace-view">
      {/* Section Navigation */}
      <div className={`flex items-center gap-2 border-b pb-3 ${lightMode ? "border-gray-200" : "border-white/10"}`}>
        {sections.map((s) => (
            <button
              key={s.id}
              onClick={() => setActiveSection(s.id)}
              className={`px-4 py-2 rounded-lg text-sm font-mono transition-colors ${
                activeSection === s.id
                  ? lightMode ? "bg-[#ff3737]/10 text-[#ff3737]" : "bg-[#ff3737]/15 text-[#ff3737]"
                  : lightMode ? "text-gray-500 hover:text-gray-800 hover:bg-gray-100" : "text-[#ebebeb]/50 hover:text-[#ebebeb]/80 hover:bg-white/5"
              }`}
              data-testid={`marketplace-section-${s.id}`}
            >
              {s.label}
            </button>
          ))}
      </div>

      {/* Filters */}
      {activeSection !== "uploads" && activeSection !== "tags" && (
        <MarketplaceFilters
          filters={filters}
          onApply={handleApplyFilters}
          onExport={handleExport}
          breakdownBySSP={breakdownBySSP}
          lightMode={lightMode}
          tags={tags}
          isSuperAdmin={isSuperAdmin}
        />
      )}

      {/* Dashboard View */}
      {activeSection === "dashboard" && (
        <>
          <MarketplaceKPIs summary={summary} loading={loading} revenueVisible={revenueVisible} lightMode={lightMode} />
          <MarketplaceCharts
            breakdownBySSP={breakdownBySSP}
            trendData={trendData}
            loading={loading}
            revenueVisible={revenueVisible}
            lightMode={lightMode}
          />
          <MarketplaceDealBreakdown
            token={token}
            filters={filters}
            revenueVisible={revenueVisible}
            lightMode={lightMode}
          />
          {isSuperAdmin && (
            <MarketplaceTagBreakdown
              token={token}
              filters={filters}
              revenueVisible={revenueVisible}
            />
          )}
        </>
      )}

      {/* Data Table View */}
      {activeSection === "table" && (
        <MarketplaceTable
          token={token}
          filters={filters}
          revenueVisible={revenueVisible}
          lightMode={lightMode}
          isSuperAdmin={isSuperAdmin}
          tags={tags}
          onTagsChanged={refreshTags}
        />
      )}

      {/* Tags View (Super Admin only) */}
      {activeSection === "tags" && isSuperAdmin && (
        <DealTagsManager token={token} requestDelete={requestDelete} />
      )}

      {/* Manage Uploads View */}
      {activeSection === "uploads" && isSuperAdmin && (
        <MarketplaceUploads
          token={token}
          requestDelete={requestDelete}
          onUploadComplete={fetchDashboard}
        />
      )}
    </div>
  );
};

export default MarketplaceView;
